/* ==========================================================================
   User Profile & Cloud Sync UI Controller
   Handles user authentication by name, status indicators, and JSON import/export
   ========================================================================== */

import { appState } from './state.js';

export function initUserSync() {
  const btnUserProfile = document.getElementById('userProfileBtn');
  const userProfileName = document.getElementById('userProfileName');
  const syncDot = document.getElementById('syncDot');

  const modal = document.getElementById('userSyncModal');
  const btnClose = document.getElementById('btnCloseUserSync');
  const btnCancel = document.getElementById('btnCancelUserSync');
  const form = document.getElementById('userSyncForm');
  const userInput = document.getElementById('syncUserNameInput');
  const syncStatusText = document.getElementById('syncStatusText');
  const syncStatusBadge = document.getElementById('syncStatusBadge');
  const btnSignOut = document.getElementById('btnSignOutUser');
  const btnSyncNow = document.getElementById('btnSyncNowUser');

  const btnExport = document.getElementById('btnExportData');
  const btnImportTrigger = document.getElementById('btnImportDataTrigger');
  const importFileInput = document.getElementById('importFileInput');

  function updateHeaderBadge(state) {
    const user = state.currentUser;
    const status = state.cloudStatus || 'local';

    if (userProfileName) {
      userProfileName.textContent = user ? user : 'Sign In';
    }

    if (syncDot) {
      syncDot.className = 'sync-dot ' + status;
      if (status === 'synced') syncDot.title = 'Cloud Synced';
      else if (status === 'syncing') syncDot.title = 'Syncing with Cloud...';
      else if (status === 'offline') syncDot.title = 'Local Mode (Cloud Storage Pending Setup)';
      else syncDot.title = 'Local Device Storage';
    }
  }

  function updateModalView(state) {
    const user = state.currentUser;
    const status = state.cloudStatus || 'local';
    const lastSync = state.lastSyncedAt;

    if (userInput && document.activeElement !== userInput) {
      userInput.value = user || '';
    }

    if (btnSignOut) {
      btnSignOut.style.display = user ? 'inline-flex' : 'none';
    }
    if (btnSyncNow) {
      btnSyncNow.style.display = user ? 'inline-flex' : 'none';
    }

    if (syncStatusBadge && syncStatusText) {
      if (!user) {
        syncStatusBadge.className = 'status-badge local';
        syncStatusBadge.textContent = 'Local Device Mode';
        syncStatusText.textContent = 'Data is currently stored only in this device browser. Enter your name above to sync across devices.';
      } else if (status === 'synced') {
        syncStatusBadge.className = 'status-badge synced';
        syncStatusBadge.textContent = 'Cloud Synced ✓';
        const formattedTime = lastSync ? new Date(lastSync).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : 'just now';
        syncStatusText.textContent = `Signed in as "${user}". Your data is saved to Cloudflare and synced across all your devices (Last synced: ${formattedTime}).`;
      } else if (status === 'syncing') {
        syncStatusBadge.className = 'status-badge syncing';
        syncStatusBadge.textContent = 'Syncing...';
        syncStatusText.textContent = `Connecting to Cloudflare to sync data for user "${user}"...`;
      } else if (status === 'offline') {
        syncStatusBadge.className = 'status-badge offline';
        syncStatusBadge.textContent = 'Local Saved (Cloud Offline)';
        syncStatusText.textContent = `Signed in as "${user}". Saved locally on this device. (Cloudflare KV will sync automatically once bound in dashboard).`;
      } else {
        syncStatusBadge.className = 'status-badge local';
        syncStatusBadge.textContent = 'Local Mode';
        syncStatusText.textContent = `User profile "${user}" active locally.`;
      }
    }
  }

  // Initial render & state subscription
  updateHeaderBadge(appState.get());
  appState.subscribe(state => {
    updateHeaderBadge(state);
    if (modal && !modal.classList.contains('hidden')) {
      updateModalView(state);
    }
  });

  // Open modal
  if (btnUserProfile) {
    btnUserProfile.addEventListener('click', () => {
      updateModalView(appState.get());
      if (modal) modal.classList.remove('hidden');
    });
  }

  // Close modal
  const closeModal = () => {
    if (modal) modal.classList.add('hidden');
  };
  if (btnClose) btnClose.addEventListener('click', closeModal);
  if (btnCancel) btnCancel.addEventListener('click', closeModal);

  // Form submit (Sign In / Switch User)
  if (form) {
    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      const name = userInput.value.trim();
      if (!name) return;
      await appState.setUser(name);
      closeModal();
    });
  }

  // Sign out button
  if (btnSignOut) {
    btnSignOut.addEventListener('click', async () => {
      await appState.setUser('');
      closeModal();
    });
  }

  // Sync now button
  if (btnSyncNow) {
    btnSyncNow.addEventListener('click', async () => {
      await appState.syncAll();
    });
  }

  // Export JSON
  if (btnExport) {
    btnExport.addEventListener('click', () => {
      appState.exportJson();
    });
  }

  // Import JSON
  if (btnImportTrigger && importFileInput) {
    btnImportTrigger.addEventListener('click', () => {
      importFileInput.click();
    });

    importFileInput.addEventListener('change', (e) => {
      const file = e.target.files[0];
      if (!file) return;

      const reader = new FileReader();
      reader.onload = (event) => {
        const success = appState.importJson(event.target.result);
        if (success) {
          alert('Data imported successfully!');
          closeModal();
        } else {
          alert('Failed to import data. Please check that the file is valid JSON.');
        }
      };
      reader.readAsText(file);
      importFileInput.value = '';
    });
  }
}
